import { useEffect, useState } from 'react';
import { FileText, CreditCard, Download, Landmark, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { storeApi } from '../../lib/api';
import { formatAUD } from './CartContext';

function usePaymentPoll(sessionId, onPaid) {
  const [state, setState] = useState(sessionId ? 'checking' : 'idle');
  useEffect(() => {
    if (!sessionId) return undefined;
    let attempts = 0, cancelled = false;
    const tick = async () => {
      try {
        const { data } = await storeApi.paymentStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === 'paid') { setState('paid'); onPaid(); return; }
        if (['failed', 'expired'].includes(data.payment_status)) { setState('failed'); return; }
      } catch { /* keep polling */ }
      if (++attempts < 6 && !cancelled) setTimeout(tick, 2000); else if (!cancelled) setState('timeout');
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, onPaid]);
  return state;
}

export function InvoiceCard({ order, sessionId, paymentCancelled, onPaid }) {
  const inv = order.invoice;
  const [bank, setBank] = useState(null);
  const [paying, setPaying] = useState(false);
  const pollState = usePaymentPoll(sessionId, onPaid);
  const paid = inv.payment_status === 'paid';

  useEffect(() => { storeApi.getInvoiceSettings().then((r) => setBank(r.data)).catch(() => setBank({})); }, []);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await storeApi.createInvoiceCheckout(order.order_number, order.email);
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start payment'); setPaying(false); }
  };

  return (
    <div className={`rounded-2xl p-6 lg:p-8 space-y-6 ${paid ? 'bg-white' : 'text-white'}`} style={paid ? { border: '1px solid var(--s-sand)' } : { background: 'var(--s-green)' }} data-testid="invoice-card">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-[0.2em] mb-1 ${paid ? '' : 'text-white/60'}`} style={paid ? { color: 'var(--s-terra)' } : undefined}>Invoice {inv.number}</p>
          <p className="text-3xl font-heading font-bold" data-testid="invoice-amount">{formatAUD(inv.amount)}</p>
          <p className={`text-sm mt-1 ${paid ? '' : 'text-white/70'}`} style={paid ? { color: 'var(--s-muted)' } : undefined}>
            {paid ? `Paid ${inv.paid_at?.slice(0, 10)} — thank you!` : <>Due <strong>{inv.due_date}</strong>{inv.due_before_delivery ? ` · before your ${order.delivery_method === 'pickup' ? 'pickup' : 'delivery'}` : ''}</>}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${paid ? 'bg-green-100 text-green-800' : 'bg-white/15 text-white'}`} data-testid="invoice-status">
          {paid ? <CheckCircle2 className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />} {paid ? 'Paid' : 'Awaiting payment'}
        </span>
      </div>

      {sessionId && (pollState === 'paid' || paid) && (
        <p className="flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-3 bg-green-100 text-green-800" data-testid="payment-success"><CheckCircle2 className="h-4 w-4" /> Payment received — thank you! We'll be in touch to confirm your {order.delivery_method === 'pickup' ? 'pickup' : 'delivery'}.</p>
      )}
      {sessionId && pollState === 'checking' && !paid && <p className="flex items-center gap-2 text-sm text-white/80" data-testid="payment-checking"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…</p>}
      {sessionId && pollState === 'timeout' && !paid && <p className="text-sm text-orange-200" data-testid="payment-timeout">We're still waiting on confirmation from the bank. Refresh in a moment — your payment may still be processing.</p>}
      {sessionId && pollState === 'failed' && <p className="flex items-center gap-2 text-sm text-orange-200" data-testid="payment-failed"><XCircle className="h-4 w-4" /> That payment didn't go through. You can try again below.</p>}
      {paymentCancelled && !paid && <p className="text-sm text-orange-200" data-testid="payment-cancelled">Payment was cancelled — no charge was made.</p>}

      {!paid && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl p-4 bg-white/10">
            <p className="font-heading font-bold flex items-center gap-2 mb-2"><CreditCard className="h-4 w-4" /> Pay now by card</p>
            <p className="text-xs text-white/70 mb-3">Secure checkout. Visa, Mastercard and Amex accepted.</p>
            <button onClick={payNow} disabled={paying} className="store-btn store-btn-primary w-full py-2.5" data-testid="pay-now-btn">{paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay {formatAUD(inv.amount)}</button>
          </div>
          <div className="rounded-xl p-4 bg-white/10 text-sm">
            <p className="font-heading font-bold flex items-center gap-2 mb-2"><Landmark className="h-4 w-4" /> Bank transfer</p>
            {bank === null ? (
              <div className="space-y-2 animate-pulse" data-testid="bank-details-loading">{[0, 1, 2].map((i) => <div key={i} className="h-3 rounded bg-white/20" />)}</div>
            ) : bank?.bsb ? (
              <dl className="text-xs text-white/80 space-y-0.5" data-testid="bank-details">
                {bank.bank_name && <div className="flex justify-between gap-2"><dt>Bank</dt><dd className="font-medium text-white">{bank.bank_name}</dd></div>}
                {bank.account_name && <div className="flex justify-between gap-2"><dt>Name</dt><dd className="font-medium text-white">{bank.account_name}</dd></div>}
                <div className="flex justify-between gap-2"><dt>BSB</dt><dd className="font-medium text-white">{bank.bsb}</dd></div>
                <div className="flex justify-between gap-2"><dt>Account</dt><dd className="font-medium text-white">{bank.account_number}</dd></div>
                <div className="flex justify-between gap-2"><dt>Reference</dt><dd className="font-medium text-white">{inv.number}</dd></div>
              </dl>
            ) : <p className="text-xs text-white/70">Bank details are on your PDF invoice, or call us and we'll send them through.</p>}
          </div>
        </div>
      )}

      <a href={storeApi.invoicePdfUrl(order.order_number, order.email)} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 text-sm font-semibold ${paid ? '' : 'text-white/90'} hover:underline`} style={paid ? { color: 'var(--s-green)' } : undefined} data-testid="download-invoice-pdf">
        <Download className="h-4 w-4" /> Download invoice PDF
      </a>
    </div>
  );
}
